// TaskInspectModal.js
// Asana-like task detail view modal for inspecting and editing tasks

import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { CONCIERGE_SENDERS } from '../contexts/AdminContext';

function TaskInspectModal({
  task,
  onClose,
  onAutoSave,
  familyData,
  currentUserId,
  currentUserDisplayName,
  isPremium,
  isAdminMode = false
}) {
  // Editable state - initialize from task
  const [title, setTitle] = useState(task.title || '');
  const [isCompleted, setIsCompleted] = useState(task.status === 'done');
  const [assignee, setAssignee] = useState(task.assignee || '');
  const [dueDate, setDueDate] = useState(task.dueDate || '');
  const [notes, setNotes] = useState(task.notes || '');

  // Comments state
  const [comments, setComments] = useState(task.comments || []);
  const [commentText, setCommentText] = useState('');

  // Concierge sender selection (for admin mode)
  const [conciergeSender, setConciergeSender] = useState(CONCIERGE_SENDERS[0]?.id || 'kana');

  // @ mention state
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionMenuPosition, setMentionMenuPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  const commentInputRef = useRef(null);
  const modalRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);
  const prevCompletedRef = useRef(task.status === 'done');

  // Get family members for assignee selector and mentions
  const memberNames = familyData?.memberNames || {};
  const members = Object.entries(memberNames).sort((a, b) => {
    if (a[0] === currentUserId) return -1;
    if (b[0] === currentUserId) return 1;
    return a[1].localeCompare(b[1]);
  });

  // Get other family members (not current user) for @ mentions
  const otherMembers = members.filter(([userId]) => userId !== currentUserId);

  // Helper to get assignee name
  const getAssigneeName = (assigneeId) => {
    if (assigneeId === 'ai') {
      return 'AI Assistant';
    } else if (assigneeId && memberNames[assigneeId]) {
      return memberNames[assigneeId];
    }
    return '';
  };

  // Autosave effect - debounced save when fields change
  useEffect(() => {
    // Skip initial mount to avoid saving on open
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounced save
    debounceTimerRef.current = setTimeout(() => {
      onAutoSave({
        ...task,
        title,
        status: isCompleted ? 'done' : 'pending',
        assignee,
        assigneeName: getAssigneeName(assignee),
        dueDate: dueDate || null,
        notes,
        comments
      });
    }, 500);

    // Cleanup timer on unmount or before next effect
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [title, isCompleted, assignee, dueDate, notes]);

  // Create notification when task is marked as done
  useEffect(() => {
    // Check if task just transitioned to completed
    if (isCompleted && !prevCompletedRef.current) {
      // Create notification
      const createNotification = async () => {
        try {
          await addDoc(collection(db, 'notifications'), {
            familyId: task.familyId,
            type: 'task_completed',
            actorId: currentUserId,
            actorName: currentUserDisplayName,
            taskId: task.id,
            taskTitle: title,
            readBy: [],
            createdAt: serverTimestamp()
          });
        } catch (error) {
          console.error('Error creating completion notification:', error);
        }
      };
      createNotification();
    }
    prevCompletedRef.current = isCompleted;
  }, [isCompleted, task.familyId, task.id, title, currentUserId, currentUserDisplayName]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showMentionMenu) {
          setShowMentionMenu(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showMentionMenu]);

  // Handle click outside modal to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle comment text change with @ mention detection
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setCommentText(value);

    // Check if user just typed @
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Show menu if @ is at the end or followed only by word characters
      if (textAfterAt === '' || /^\w*$/.test(textAfterAt)) {
        setMentionStartIndex(lastAtIndex);
        setShowMentionMenu(true);

        // Position the menu near the textarea
        if (commentInputRef.current) {
          const rect = commentInputRef.current.getBoundingClientRect();
          setMentionMenuPosition({
            top: rect.bottom + 4,
            left: rect.left
          });
        }
        return;
      }
    }

    setShowMentionMenu(false);
    setMentionStartIndex(-1);
  };

  // Handle mention selection
  const handleSelectMention = (name) => {
    if (mentionStartIndex !== -1) {
      const beforeMention = commentText.substring(0, mentionStartIndex);
      const cursorPos = commentInputRef.current?.selectionStart || commentText.length;
      const afterCursor = commentText.substring(cursorPos);

      const newText = `${beforeMention}@${name} ${afterCursor}`;
      setCommentText(newText);
    }

    setShowMentionMenu(false);
    setMentionStartIndex(-1);

    // Refocus the textarea
    setTimeout(() => {
      commentInputRef.current?.focus();
    }, 0);
  };

  // Handle comment submission - saves immediately
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;

    // Determine if this is a concierge comment or regular user comment
    const isConciergeComment = isAdminMode;
    const selectedSender = CONCIERGE_SENDERS.find(s => s.id === conciergeSender);
    const senderName = isConciergeComment ? (selectedSender?.name || 'Kana') : currentUserDisplayName;

    // Build comment object
    // For concierge comments, add authorType and authorDisplayName
    const newComment = {
      id: Date.now().toString(),
      name: senderName,
      timestamp: new Date().toLocaleString(),
      text: commentText.trim(),
      // Extended fields for concierge comments
      ...(isConciergeComment && {
        authorType: 'concierge',
        authorDisplayName: senderName,
        authorId: conciergeSender, // 'kana' or 'david'
        meta: {
          createdByAdminId: currentUserId
        }
      }),
      // For regular comments, default authorType
      ...(!isConciergeComment && {
        authorType: 'user',
        authorId: currentUserId
      })
    };

    const updatedComments = [...comments, newComment];
    setComments(updatedComments);
    setCommentText('');

    // Save immediately (don't wait for debounce)
    onAutoSave({
      ...task,
      title,
      status: isCompleted ? 'done' : 'pending',
      assignee,
      assigneeName: getAssigneeName(assignee),
      dueDate: dueDate || null,
      notes,
      comments: updatedComments
    });

    // Create notification for the comment
    // For concierge comments: use concierge name, don't set actorId to a real user
    // so no one is excluded from seeing the notification
    try {
      await addDoc(collection(db, 'notifications'), {
        familyId: task.familyId,
        type: 'comment_added',
        // For concierge comments, use a special actorId that won't match any real user
        actorId: isConciergeComment ? `concierge_${conciergeSender}` : currentUserId,
        actorName: senderName,
        taskId: task.id,
        taskTitle: title,
        readBy: [],
        createdAt: serverTimestamp(),
        // Additional metadata for concierge comments
        ...(isConciergeComment && {
          isConcierge: true,
          conciergeId: conciergeSender
        })
      });
    } catch (error) {
      console.error('Error creating comment notification:', error);
    }
  };

  // Handle Enter key in comment input (submit on Enter, newline on Shift+Enter)
  const handleCommentKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    return timestamp;
  };

  return (
    <div className="task-inspect-overlay" onClick={handleOverlayClick}>
      <div className="task-inspect-modal" ref={modalRef}>
        {/* Header row */}
        <div className="task-inspect-header">
          <div
            className={`task-inspect-checkbox ${isCompleted ? 'checked' : ''}`}
            onClick={() => setIsCompleted(!isCompleted)}
            role="checkbox"
            aria-checked={isCompleted}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsCompleted(!isCompleted);
              }
            }}
          />
          <input
            type="text"
            className="task-inspect-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
          />
          <button
            className="task-inspect-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body content */}
        <div className="task-inspect-body">
          {/* Metadata section */}
          <div className="task-inspect-metadata">
            {/* Assignee field */}
            <div className="task-inspect-field">
              <label htmlFor="task-assignee">Assignee</label>
              <select
                id="task-assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
              >
                <option value="">Unassigned</option>
                {members.map(([userId, firstName]) => (
                  <option key={userId} value={userId}>
                    {firstName}
                    {userId === currentUserId ? ' (me)' : ''}
                  </option>
                ))}
                <option value="ai">
                  Kana{!isPremium ? ' (Premium)' : ''}
                </option>
              </select>
            </div>

            {/* Due date field */}
            <div className="task-inspect-field">
              <label htmlFor="task-due-date">Due date</label>
              <input
                type="date"
                id="task-due-date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Notes section */}
          <div className="task-inspect-section">
            <label htmlFor="task-notes">Notes</label>
            <textarea
              id="task-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What is this task about?"
              className="task-inspect-notes"
            />
          </div>

          {/* Comments section */}
          <div className="task-inspect-section">
            <label>Comments</label>

            {/* Comments list */}
            <div className="task-inspect-comments-list">
              {comments.length === 0 ? (
                <div className="task-inspect-no-comments">
                  No comments yet
                </div>
              ) : (
                comments.map((comment) => {
                  const isConcierge = comment.authorType === 'concierge';
                  return (
                    <div key={comment.id} className={`task-inspect-comment ${isConcierge ? 'concierge' : ''}`}>
                      <div className="task-inspect-comment-header">
                        <span className={`task-inspect-comment-name ${isConcierge ? 'concierge' : ''}`}>
                          {comment.name}
                        </span>
                        <span className="task-inspect-comment-timestamp">
                          {formatTimestamp(comment.timestamp)}
                        </span>
                      </div>
                      <div className="task-inspect-comment-text">{comment.text}</div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Comment composer */}
            <div className="task-inspect-comment-composer">
              {/* Concierge sender selector - only in admin mode */}
              {isAdminMode && (
                <div className="concierge-sender-selector">
                  <label>Post as:</label>
                  <div className="concierge-sender-options">
                    {CONCIERGE_SENDERS.map((sender) => (
                      <button
                        key={sender.id}
                        type="button"
                        className={`concierge-sender-option ${conciergeSender === sender.id ? 'active' : ''}`}
                        onClick={() => setConciergeSender(sender.id)}
                      >
                        {sender.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <textarea
                ref={commentInputRef}
                value={commentText}
                onChange={handleCommentChange}
                onKeyDown={handleCommentKeyDown}
                placeholder={isAdminMode ? `Add a comment as ${CONCIERGE_SENDERS.find(s => s.id === conciergeSender)?.name || 'Kana'}` : "Add a comment"}
                className="task-inspect-comment-input"
              />
              <button
                className="button button-primary button-small task-inspect-comment-submit"
                onClick={handleSubmitComment}
                disabled={!commentText.trim()}
              >
                Post
              </button>

              {/* @ mention menu */}
              {showMentionMenu && (
                <div
                  className="task-inspect-mention-menu"
                  style={{
                    position: 'fixed',
                    top: mentionMenuPosition.top,
                    left: mentionMenuPosition.left
                  }}
                >
                  {otherMembers.map(([userId, firstName]) => (
                    <button
                      key={userId}
                      className="task-inspect-mention-option"
                      onClick={() => handleSelectMention(firstName)}
                    >
                      {firstName}
                    </button>
                  ))}
                  <button
                    className="task-inspect-mention-option"
                    onClick={() => handleSelectMention('Kana')}
                  >
                    Kana
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default TaskInspectModal;
